import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import * as state from '@/components/Analysis/state';

import { AlarmLevelBox, ColumnTitleTypography, RowValueTypography, tableCellStyle } from './styles';
import {
  PointOfInterestTemplateField,
  ReportDetailsQuery,
  UnitsEnum,
} from '@/__generated__/graphql';
import { convertSquareFeetToSquareMeters } from '@/utils/unitSystem';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { titleCaseSentence } from '@/utils';
import { abyssColors } from '@/theme/colors';

type StringObject = {
  [key: string]: string;
};

type PaintBlockReportTableProps = {
  columns: PointOfInterestTemplateField[];
  rows: StringObject[];
  isEditMode: boolean;
  setEditSelectedReport?: Dispatch<SetStateAction<ReportDetailsQuery['report'] | undefined>>;
  setSelectedChaseItems?: Dispatch<SetStateAction<string[]>>;
  selectedChaseItems?: string[];
  allItemsChecked?: boolean;
  tableName?: string;
};

const PaintBlockReportHead = ({
  columns,
  tableName,
  setSelectedChaseItems,
  selectedChaseItems,
  rows,
}: {
  columns: PointOfInterestTemplateField[];
  tableName?: string;
  setSelectedChaseItems?: Dispatch<SetStateAction<string[]>>;
  selectedChaseItems?: string[];
  rows: StringObject[];
}) => {
  const selectAllItems = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!setSelectedChaseItems) return;

      if (selectedChaseItems?.length === rows.length) {
        setSelectedChaseItems([]);
        return;
      }

      setSelectedChaseItems(event.target.checked ? rows.map((row) => row.id) : []);
    },
    [rows, selectedChaseItems, setSelectedChaseItems]
  );

  return (
    <TableHead>
      <TableRow>
        {tableName === 'chaseItems' && (
          <TableCell
            sx={{
              p: 1,
              background: abyssColors.primary[100],
            }}
          >
            <Checkbox
              data-testid="chaseItems-select-all-checkbox"
              onChange={(event) => {
                selectAllItems(event);
              }}
              inputProps={{ 'aria-label': 'select all rows' }}
            />
          </TableCell>
        )}
        {columns &&
          columns.map((column, index) => (
            <TableCell
              sx={{
                p: 1,
                background: abyssColors.primary[100],
              }}
              key={column.name + index}
            >
              <ColumnTitleTypography> {titleCaseSentence(column.name)}</ColumnTitleTypography>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
};

function CustomTableCell({
  value,
  index,
  name,
  onValueChange,
  isEditMode,
  tableName,
}: {
  value: string;
  index: number;
  name: string;
  onValueChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isEditMode?: boolean;
  tableName?: string;
}) {
  const isEditAndAssetTypeCol = index !== 0 && isEditMode;
  if (isEditAndAssetTypeCol) {
    return (
      <TableCell
        style={tableCellStyle}
        sx={isEditMode ? { border: `1px solid ${abyssColors.primary[200]} !important` } : {}}
      >
        <TextField
          variant="outlined"
          size="small"
          value={Number.isNaN(Number(value)) ? '' : value}
          onChange={(event) => onValueChange(event)}
          error={!/^(N\/A|\d*\.?\d*)$/.test(value || '')}
          helperText={!/^(N\/A|\d*\.?\d*)$/.test(value || '') ? 'Only numbers are allowed' : ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: 0,
              height: 'auto',
              border: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              display: 'none',
            },
            '& .MuiInputBase-input': {
              padding: 0,
              margin: 0,
              fontSize: '14px',
              fontWeight: 400,
              color: abyssColors.primary[900],
              lineHeight: 1.5,
            },
          }}
        />
      </TableCell>
    );
  }

  if (name === 'Alarm Level') {
    return (
      <TableCell style={tableCellStyle}>
        {value !== '' && value !== 'N/A' ? (
          <>
            <AlarmLevelBox
              level={value}
              sx={{
                marginLeft: '11px',
              }}
            />
            {value}
          </>
        ) : (
          <RowValueTypography isNA={true}>{'N/A'}</RowValueTypography>
        )}
      </TableCell>
    );
  }

  return (
    <TableCell
      style={tableCellStyle}
      sx={{
        background: name === 'Asset type' ? abyssColors.primary[50] : 'inherit',
        ...(isEditMode && { borderRight: `1px solid ${abyssColors.primary[300]} !important` }),
      }}
    >
      {tableName === 'actualValues' ? (
        <RowValueTypography isNA={false}>
          {value === '' || value === 'N/A' ? '' : value}
        </RowValueTypography>
      ) : (
        <RowValueTypography isNA={value === '' || value === 'N/A'}>
          {value === '' ? 'N/A' : value}
        </RowValueTypography>
      )}
    </TableCell>
  );
}

const PaintBlockReportBody = ({
  rows,
  columns,
  isEditMode,
  setEditSelectedReport,
  setSelectedChaseItems,
  selectedChaseItems,
  tableName,
}: PaintBlockReportTableProps) => {
  const [currentEdit, setCurrentEdit] = useState<StringObject[]>([]);
  const unitSystem = useAtomValue(state.unitSystem);

  useEffect(() => {
    setCurrentEdit(rows);
  }, [isEditMode, rows]);

  const handleValueChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    rowIndex: number
  ) => {
    if (!/^\d*\.?\d*$/.test(event.target.value) || !setEditSelectedReport) return;
    const updatedState = [...currentEdit];
    const updatedRow = { ...updatedState[rowIndex], [key]: event.target.value };
    updatedState[rowIndex] = updatedRow;

    setCurrentEdit(updatedState);

    if (
      Object.values(updatedState[rowIndex]).some((value, index) => {
        if (index > 0 && !/^(N\/A|\d*\.?\d*)$/.test(value)) {
          return true;
        }
        return false;
      })
    ) {
      return;
    }
    const templateFieldData: StringObject = {};

    const rowData = updatedState[rowIndex];
    const selectedAssetType = rowData['Asset type']?.toLowerCase();

    columns.forEach((col) => {
      if (col.key === 'asset_type') return;

      const fieldValue = rowData[col.name];
      const selectedKey = `${selectedAssetType}_${col.key}`;

      if (fieldValue === 'N/A') return;

      if (col.unit === UnitsEnum.SquareMeter && unitSystem === UnitSystemEnum.Imperial) {
        templateFieldData[selectedKey] = convertSquareFeetToSquareMeters(Number(fieldValue)).toString();
      } else {
        templateFieldData[selectedKey] = fieldValue;
      }
    });

    setEditSelectedReport((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        templateFieldData: {
          ...previous.templateFieldData,
          ...templateFieldData,
        },
      };
    });
  };

  const handleCheckboxChange = useCallback(
    (id: string) => {
      if (setSelectedChaseItems) {
        setSelectedChaseItems((previousValue) => {
          if (!previousValue) previousValue = [];
          return previousValue.includes(id)
            ? previousValue.filter((item) => item !== id)
            : [...previousValue, id];
        });
      }
    },
    [setSelectedChaseItems]
  );

  return (
    <TableBody>
      {rows &&
        rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {tableName === 'chaseItems' && (
              <TableCell style={tableCellStyle}>
                <Checkbox
                  data-testid={`chaseItem-checkbox-${rowIndex}`}
                  checked={selectedChaseItems?.includes(row.id) || false}
                  onChange={() => {
                    handleCheckboxChange(row.id);
                  }}
                />
              </TableCell>
            )}
            {columns.map((a, cellIndex) => {
              const cellKey = a.name;
              const cellValue =
                currentEdit && currentEdit[rowIndex]?.[cellKey] !== undefined
                  ? currentEdit[rowIndex][cellKey]
                  : row[cellKey];
              return (
                <CustomTableCell
                  key={cellIndex}
                  index={cellIndex}
                  value={Object.prototype.hasOwnProperty.call(row, cellKey) ? cellValue : ''}
                  name={cellKey}
                  onValueChange={(event) => handleValueChange(event, cellKey, rowIndex)}
                  isEditMode={isEditMode}
                  tableName={tableName}
                />
              );
            })}
          </TableRow>
        ))}
    </TableBody>
  );
};

export const PaintBlockReportTable = ({
  columns,
  rows,
  isEditMode,
  setEditSelectedReport,
  setSelectedChaseItems,
  selectedChaseItems,
  tableName,
}: PaintBlockReportTableProps) => {
  return (
    <>
      <Box sx={{ overflowX: 'auto', marginLeft: '20px', width: '98%' }}>
        <Table sx={{ minWidth: 650 }} data-testid={tableName}>
          <PaintBlockReportHead
            columns={columns}
            tableName={tableName}
            setSelectedChaseItems={setSelectedChaseItems}
            selectedChaseItems={selectedChaseItems}
            rows={rows}
          />
          <PaintBlockReportBody
            rows={rows}
            columns={columns}
            isEditMode={isEditMode}
            setEditSelectedReport={setEditSelectedReport}
            setSelectedChaseItems={setSelectedChaseItems}
            selectedChaseItems={selectedChaseItems}
            tableName={tableName}
          />
        </Table>
      </Box>
    </>
  );
};
